/**
 * Add/Edit Team modal. Single component handles both flows — pass `team`
 * to edit, omit to add.
 *
 * Client-side enforcement mirrors the server Zod contract:
 *   - skillLevel: hard-enum dropdown (Beginner | Intermediate | Advanced).
 *   - coachId: dropdown populated from GET /api/coaches; submits the
 *     numeric id, not the email.
 *   - currentEnrollment / isActive / academyId NEVER appear in this form
 *     (server-side stripping is the real gate, but matching here keeps
 *     the contract obvious).
 *
 * Data-testids per the PR-X1 spec:
 *   team-modal, input-team-name, select-skill-level, select-age-group,
 *   input-age-range, select-team-coach, input-max-capacity, input-venue,
 *   input-schedule, btn-save-team, btn-cancel-team.
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useMutation } from "@tanstack/react-query";
import { asArray } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast, notify } from "@/shared/toast";
import { isPendingLike } from "@/shared/pending";

const SKILL_LEVELS = ["Beginner", "Intermediate", "Advanced"] as const;
type SkillLevel = (typeof SKILL_LEVELS)[number];

// Age group is a free-text field (academies have varying labels) — the
// server accepts any non-empty string. The dropdown's canonical values
// are the academy's own labels: Foundation / Development / Elite per the
// product call, but custom is allowed.
const CANONICAL_AGE_GROUPS = ["Foundation", "Development", "Elite"] as const;

const teamFormSchema = z.object({
  name: z.string().min(1, "Team name is required").max(255),
  ageGroup: z.string().min(1, "Age group is required").max(255),
  ageRange: z.string().min(1, "Age range is required").max(10),
  skillLevel: z.enum(SKILL_LEVELS, {
    errorMap: () => ({ message: "Select a skill level" }),
  }),
  coachId: z.number({ invalid_type_error: "Coach is required" }).int().positive(),
  schedule: z.string().max(255).optional().or(z.literal("")),
  venue: z.string().max(255).optional().or(z.literal("")),
  maxCapacity: z.coerce.number().int().min(1).max(100).optional(),
});

type TeamFormValues = z.infer<typeof teamFormSchema>;

export interface TeamForEdit {
  id: number;
  name: string;
  ageGroup: string;
  ageRange: string;
  skillLevel: SkillLevel | null;
  coachId: number;
  schedule: string | null;
  venue: string | null;
  maxCapacity: number | null;
}

interface CoachOption {
  id: number;
  fullName: string;
  email: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team?: TeamForEdit; // present => edit mode; absent => add
}

export function AddEditTeamDialog({ open, onOpenChange, team }: Props) {
  const isEdit = !!team;
  const { toast } = useToast();
  const safeToast = (t: any) => { try { toast(t); } catch { notify(t); } };

  // Load coaches for the dropdown. RBAC-narrow payload: {id, fullName, email}.
  const { data: coachesResp, isLoading: coachesLoading } = useQuery({
    queryKey: ["/api/coaches"],
    queryFn: async () => {
      const r: any = await apiRequest("GET", "/api/coaches");
      return r;
    },
    enabled: open, // only fetch when the modal opens
  });
  const coaches: CoachOption[] = asArray<CoachOption>(coachesResp?.data);

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: {
      name: "",
      ageGroup: "",
      ageRange: "",
      skillLevel: "Beginner",
      coachId: undefined as any, // forces user to pick
      schedule: "",
      venue: "",
      maxCapacity: 20,
    },
  });

  // Reset whenever the modal opens — populates edit values or clears for add.
  useEffect(() => {
    if (!open) return;
    if (team) {
      form.reset({
        name: team.name,
        ageGroup: team.ageGroup,
        ageRange: team.ageRange,
        skillLevel: (team.skillLevel ?? "Beginner") as SkillLevel,
        coachId: team.coachId,
        schedule: team.schedule ?? "",
        venue: team.venue ?? "",
        maxCapacity: team.maxCapacity ?? 20,
      });
    } else {
      form.reset({
        name: "",
        ageGroup: "",
        ageRange: "",
        skillLevel: "Beginner",
        coachId: undefined as any,
        schedule: "",
        venue: "",
        maxCapacity: 20,
      });
    }
  }, [open, team, form]);

  const saveMutation = useMutation({
    mutationFn: async (data: TeamFormValues) => {
      const payload = {
        name: data.name,
        ageGroup: data.ageGroup,
        ageRange: data.ageRange,
        skillLevel: data.skillLevel,
        coachId: data.coachId,
        schedule: data.schedule || null,
        venue: data.venue || null,
        maxCapacity: data.maxCapacity ?? undefined,
      };
      if (isEdit && team) {
        return await apiRequest("PATCH", `/api/batches/${team.id}`, payload);
      }
      return await apiRequest("POST", "/api/batches", payload);
    },
    onSuccess: () => {
      safeToast({
        title: isEdit ? "Team updated" : "Team created",
        description: isEdit
          ? "Changes saved."
          : "Your new team has been added to Team Management.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/batches"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      safeToast({
        title: isEdit ? "Failed to update team" : "Failed to create team",
        description: error.message || "Please try again.",
      });
    },
  });

  function onSubmit(values: TeamFormValues) {
    saveMutation.mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="team-modal"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Team" : "Add Team"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the team's information."
              : "Create a new training group. Skill level guides placement; age range is for guidance only."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Team name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Team Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="U10 Beginners — Batch A"
                        data-testid="input-team-name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Skill level — hard enum */}
              <FormField
                control={form.control}
                name="skillLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill Level *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-skill-level">
                          <SelectValue placeholder="Select skill level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SKILL_LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Age group — free text but with canonical suggestions */}
              <FormField
                control={form.control}
                name="ageGroup"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Age Group *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-age-group">
                          <SelectValue placeholder="Foundation / Development / Elite" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CANONICAL_AGE_GROUPS.map((g) => (
                          <SelectItem key={g} value={g}>
                            {g}
                          </SelectItem>
                        ))}
                        {/* If team has a legacy / custom value, show it. */}
                        {team?.ageGroup &&
                          !CANONICAL_AGE_GROUPS.includes(team.ageGroup as any) && (
                            <SelectItem value={team.ageGroup}>
                              {team.ageGroup} (legacy)
                            </SelectItem>
                          )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Age range — short guidance text */}
              <FormField
                control={form.control}
                name="ageRange"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Age Range (guidance) *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="5–8, 9–12, 13+"
                        data-testid="input-age-range"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Coach — required */}
              <FormField
                control={form.control}
                name="coachId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Coach *</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(Number(v))}
                      value={field.value ? String(field.value) : ""}
                      disabled={coachesLoading}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-team-coach">
                          <SelectValue
                            placeholder={
                              coachesLoading ? "Loading…" : "Select a coach"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {coaches.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.fullName} <span className="text-slate-500 text-xs">({c.email})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Max capacity */}
              <FormField
                control={form.control}
                name="maxCapacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Capacity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        placeholder="20"
                        data-testid="input-max-capacity"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === "" ? undefined : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Venue */}
              <FormField
                control={form.control}
                name="venue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Venue</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Prosper Indoor Facility"
                        data-testid="input-venue"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Schedule */}
              <FormField
                control={form.control}
                name="schedule"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Schedule</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Tue & Thu, 6:00–7:30 PM"
                        data-testid="input-schedule"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="btn-cancel-team"
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPendingLike(saveMutation)}
                data-testid="btn-save-team"
              >
                {isPendingLike(saveMutation) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? "Update Team" : "Create Team"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
